
import { ShapeType, Vector2D } from './types';

export class ShapeUtils {
  
  public static isLinearShape(shape: ShapeType): boolean {
      const linearShapes: ShapeType[] = [
          'SQUARE', 'TRIANGLE', 'PENTAGON', 'HEXAGON', 'HEPTAGON', 'OCTAGON', 
          'NONAGON', 'DECAGON', 'STAR_5', 'STAR_6', 'STAR_8', 
          'INVERTED_TRIANGLE', 'DIAMOND', 'CROSS', 'GEM', 'SHIELD'
      ];
      return linearShapes.includes(shape);
  }

  /**
   * Generates a Look-Up Table (LUT) for the radius of a shape at 0..2PI.
   * This allows O(1) access to geometry instead of expensive trigonometry.
   */
  public static generateUnitRadiusLUT(shapeA: ShapeType, shapeB: ShapeType, t: number, resolution: number): Float32Array {
      const lut = new Float32Array(resolution);
      const step = (Math.PI * 2) / resolution;
      
      for(let i = 0; i < resolution; i++) {
          const angle = i * step;
          // Calculate radius for a Unit size (size = 1.0)
          // We apply the rotation offset inside the radius calculation usually, 
          // but here we want the raw shape upright at 0 rotation.
          // Note: getInterpolatedRadiusAtAngle handles the internal shape rotation logic.
          lut[i] = this.getInterpolatedRadiusAtAngle(angle, shapeA, shapeB, t, 1.0);
      }
      return lut;
  }

  /**
   * Returns the distance from the center to the shape's boundary at a given polar angle.
   * This uses exact polar equations where possible to ensure visual and physical consistency.
   * 
   * @param angle The polar angle in radians (0 is right, PI/2 is down in canvas coords)
   * @param shape The selected shape type
   * @param size The "radius" or scale of the shape
   * @param baseSize Optional base size for additive offset logic (size becomes total size)
   */
  public static getRadiusAtAngle(angle: number, shape: ShapeType, size: number, baseSize?: number): number {
    const t = angle + Math.PI / 2; // Rotate so "Up" corresponds to geometric 0 for some formulas
    let rUnit = 1.0;

    switch(shape) {
        case 'CIRCLE': rUnit = 1.0; break;
        
        case 'SQUARE': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 4, Math.PI/4); break; // Rotate to be axis aligned
        case 'TRIANGLE': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 3, -Math.PI/6); break; // Point up
        case 'PENTAGON': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 5, -Math.PI/10); break;
        case 'HEXAGON': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 6, 0); break; // Flat top?
        case 'HEPTAGON': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 7, -Math.PI/14); break;
        case 'OCTAGON': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 8, Math.PI/8); break;
        case 'NONAGON': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 9, -Math.PI/18); break;
        case 'DECAGON': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 10, 0); break;
        
        case 'INVERTED_TRIANGLE': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 3, Math.PI/6); break;
        case 'DIAMOND': rUnit = ShapeUtils.getPolygonUnitRadius(angle, 4, 0); break; // Points axis aligned

        case 'HEART': {
            // Robust Polar Heart approximation
            const theta = angle - Math.PI/2 + Math.PI; // Corrected to point down (rotate 180 deg from old default)
            const rRaw = 2 - 2 * Math.sin(theta) + Math.sin(theta) * Math.sqrt(Math.abs(Math.cos(theta))) / (Math.sin(theta) + 1.4);
            rUnit = (rRaw / 2.5);
            break; 
        }

        case 'STAR_5': rUnit = ShapeUtils.getStarUnitRadius(angle, 5, 0.5, -Math.PI/2); break;
        case 'STAR_6': rUnit = ShapeUtils.getStarUnitRadius(angle, 6, 0.55, 0); break;
        case 'STAR_8': rUnit = ShapeUtils.getStarUnitRadius(angle, 8, 0.6, 0); break;
        
        case 'CROSS': {
            rUnit = ShapeUtils.getStarUnitRadius(angle, 4, 0.35, 0);
            break;
        }

        case 'CLOVER': {
            rUnit = (0.8 + 0.2 * Math.cos(4 * angle));
            break;
        }
        
        case 'FLOWER': {
            rUnit = (0.85 + 0.15 * Math.cos(8 * angle));
            break;
        }
        
        case 'GEAR': {
            const n = 12;
            const wave = Math.sin(n * angle);
            rUnit = (0.9 + 0.1 * Math.sign(wave));
            break;
        }
        
        case 'SHIELD': {
            rUnit = ShapeUtils.getPolygonUnitRadius(angle, 3, Math.PI/6) * (1 + 0.2 * Math.cos(angle));
            break;
        }
        
        case 'GEM': {
            rUnit = ShapeUtils.getPolygonUnitRadius(angle, 6, 0) * (1 + 0.1 * Math.cos(3*angle));
            break;
        }
        default: rUnit = 1.0;
    }

    // HYBRID SCALING STRATEGY
    // Linear shapes (Polygons, Stars) must use Multiplicative Scaling to maintain parallel lines (even gaps).
    // Curved shapes (Hearts, Blobs) look better with Additive Offset to maintain visual stripe width.
    if (baseSize !== undefined && !this.isLinearShape(shape) && shape !== 'CIRCLE') {
        return (rUnit * baseSize) + (size - baseSize);
    }
    
    return rUnit * size;
  }

  // Generalized Polygon Radius Formula (Unit)
  private static getPolygonUnitRadius(theta: number, n: number, rotation: number): number {
      const a = (theta - rotation) % (2 * Math.PI / n);
      const boundary = (2 * Math.PI / n);
      let phi = (theta - rotation);
      while(phi < 0) phi += 2*Math.PI;
      const sector = 2*Math.PI/n;
      const local = (phi % sector) - sector/2;
      return Math.cos(Math.PI/n) / Math.cos(local);
  }

  // Generalized Star Radius (Unit)
  private static getStarUnitRadius(theta: number, n: number, innerRatio: number, rotation: number): number {
      const sector = Math.PI / n; 
      let phi = (theta - rotation);
      while(phi < 0) phi += 2*Math.PI;
      
      const angleStep = Math.PI / n;
      const local = phi % (2 * angleStep);
      const isFirstHalf = local < angleStep;
      
      const alpha = isFirstHalf ? local : (2*angleStep - local); // 0 to angleStep
      const R = 1.0;
      const rin = innerRatio;
      const step = angleStep;
      
      const num = R * rin * Math.sin(step);
      const den = R * Math.sin(alpha) + rin * Math.sin(step - alpha);
      return num / den;
  }

  public static getInterpolatedRadiusAtAngle(angle: number, shapeA: ShapeType, shapeB: ShapeType, t: number, size: number, baseSize?: number): number {
    // Interpolate the RESULTING radius, not just the unit radius.
    // This allows blending between "Scaled" strategy and "Offset" strategy.
    const r1 = this.getRadiusAtAngle(angle, shapeA, size, baseSize);
    const r2 = this.getRadiusAtAngle(angle, shapeB, size, baseSize);
    return r1 + (r2 - r1) * t;
  }

  public static traceInterpolatedShape(ctx: CanvasRenderingContext2D, center: Vector2D, shapeA: ShapeType, shapeB: ShapeType, t: number, size: number, baseSize?: number) {
      // Optimization: Cap resolution to 360 to prevent lag while keeping smooth corners
      const steps = Math.max(120, Math.min(360, Math.ceil(size * 1.5)));
      ctx.beginPath();
      for(let i=0; i<=steps; i++) {
          const theta = (i/steps) * Math.PI * 2;
          const r = this.getInterpolatedRadiusAtAngle(theta, shapeA, shapeB, t, size, baseSize);
          const x = center.x + Math.cos(theta) * r;
          const y = center.y + Math.sin(theta) * r;
          if (i===0) ctx.moveTo(x,y); else ctx.lineTo(x,y);
      }
      ctx.closePath();
  }

  public static traceShape(ctx: CanvasRenderingContext2D, center: Vector2D, shape: ShapeType, size: number) {
      if (shape === 'CIRCLE') {
          ctx.arc(center.x, center.y, size, 0, Math.PI*2);
          return;
      }
      this.traceInterpolatedShape(ctx, center, shape, shape, 0, size);
  }

  public static getNormal(relPos: Vector2D, shape: ShapeType, size: number, shapeB?: ShapeType, t?: number, baseSize?: number): Vector2D {
      const epsilon = 1.0;
      const dist = (p: Vector2D) => {
          const ang = Math.atan2(p.y, p.x);
          let r = 0;
          if (shapeB && t !== undefined) {
              r = this.getInterpolatedRadiusAtAngle(ang, shape, shapeB, t, size, baseSize);
          } else {
              r = ShapeUtils.getRadiusAtAngle(ang, shape, size, baseSize);
          }
          const len = Math.sqrt(p.x*p.x + p.y*p.y);
          return len - r; 
      };
      
      const d = dist(relPos);
      const nx = dist({x: relPos.x + epsilon, y: relPos.y}) - d;
      const ny = dist({x: relPos.x, y: relPos.y + epsilon}) - d;
      
      const len = Math.sqrt(nx*nx + ny*ny);
      if (len < 0.0001) return {x:0, y:0};
      return { x: nx/len, y: ny/len };
  }
}
